// Exam Prep response decryption (frontend side).
//
// The backend encrypts Exam Prep agent responses so the raw JSON isn't visible in
// the browser Network tab. Responses arrive as { success: true, enc: "<base64>" }.
// This helper decrypts `enc` back into the original object using AES-256-GCM via
// the Web Crypto API. The key must match the backend's EP_ENC_KEY.
//
// NOTE: this is obfuscation-in-transit, not true secrecy — the key ships in the
// frontend bundle. It prevents casual inspection of the payload in the Network
// tab. Anything that must be truly hidden should never be sent to the browser.

const DEFAULT_KEY = 'lb-exam-prep-response-key-v1-2026!';

let cachedKey = null;

// Derive a 32-byte AES-GCM key by SHA-256 of the shared secret (matches backend).
async function getCryptoKey() {
  if (cachedKey) return cachedKey;
  const raw = process.env.REACT_APP_EP_ENC_KEY || DEFAULT_KEY;
  const enc = new TextEncoder();
  const hash = await crypto.subtle.digest('SHA-256', enc.encode(String(raw)));
  cachedKey = await crypto.subtle.importKey('raw', hash, { name: 'AES-GCM' }, false, ['decrypt']);
  return cachedKey;
}

function base64ToBytes(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

// Decrypts a base64(iv | ciphertext | tag) string into the original JS value.
// The backend appends the GCM auth tag after the ciphertext, which is exactly the
// layout Web Crypto's AES-GCM expects (it reads the trailing 16-byte tag itself).
export async function decryptEp(encB64) {
  const key = await getCryptoKey();
  const all = base64ToBytes(encB64);
  const iv = all.slice(0, 12);
  const cipherAndTag = all.slice(12); // ciphertext + tag(16), as Web Crypto expects
  const plainBuf = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, cipherAndTag);
  const json = new TextDecoder().decode(plainBuf);
  return JSON.parse(json);
}

// Unwraps an Exam Prep API response body. If it's encrypted ({ enc }), decrypt it
// and return the underlying `data`. If it's already plaintext ({ data }) — e.g.
// an older backend or an encryption fallback — return that as-is. Returns null on
// failure so callers can handle it like a failed response.
export async function readEpResponse(body) {
  try {
    if (!body || body.success === false) return null;
    if (body.enc) {
      return await decryptEp(body.enc);
    }
    // Fallback: backend sent plaintext data (encryption disabled/failed).
    return body.data != null ? body.data : null;
  } catch (e) {
    console.error('Exam Prep response decrypt error:', e);
    return null;
  }
}
